'use client';

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { resolveSchoolId } from '../utils';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // Safe for SSR - only access localStorage on client side
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    this.client = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api/v1',
      timeout: 80000,
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        if (typeof window !== 'undefined') {
          const token = localStorage.getItem('token'); // Fixed: use 'token' key like login page
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add school context if available
        if (typeof window !== 'undefined') {
          const schoolId = resolveSchoolId();
          if (schoolId) {
            config.headers['X-School-ID'] = schoolId;
          } else if (config.headers['X-School-ID']) {
            delete config.headers['X-School-ID'];
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        
        // Handle specific error codes
        if (error.response?.status === 401) {
          // Unauthorized - redirect to login
          if (typeof window !== 'undefined') {
            localStorage.removeItem('token'); // Fixed: use 'token' key
            localStorage.removeItem('user');
            window.location.href = '/login'; // Fixed: correct login path
          }
        } else if (error.response?.status === 403) {
          console.error('Access denied. You don\'t have permission to perform this action.');
        } else if (error.response?.status === 429) {
          console.error('Too many requests. Please try again later.');
        } else if (error.response?.status >= 500) {
          console.error('Server error. Please try again later.');
        } else {
          console.error(message);
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // GET request
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  // POST request
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  // PUT request
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  // PATCH request
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  // DELETE request
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // Upload file
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }
}

// Lazy initialization to avoid SSR issues
let apiClientInstance: ApiClient | null = null;

export const apiClient = {
  get: async <T = any>(...args: Parameters<ApiClient['get']>) => {
    if (!apiClientInstance) {
      apiClientInstance = new ApiClient();
    }
    return apiClientInstance.get<T>(...args);
  },
  post: async <T = any>(...args: Parameters<ApiClient['post']>) => {
    if (!apiClientInstance) {
      apiClientInstance = new ApiClient();
    }
    return apiClientInstance.post<T>(...args);
  },
  put: async <T = any>(...args: Parameters<ApiClient['put']>) => {
    if (!apiClientInstance) {
      apiClientInstance = new ApiClient();
    }
    return apiClientInstance.put<T>(...args);
  },
  patch: async <T = any>(...args: Parameters<ApiClient['patch']>) => {
    if (!apiClientInstance) {
      apiClientInstance = new ApiClient();
    }
    return apiClientInstance.patch<T>(...args);
  },
  delete: async <T = any>(...args: Parameters<ApiClient['delete']>) => {
    if (!apiClientInstance) {
      apiClientInstance = new ApiClient();
    }
    return apiClientInstance.delete<T>(...args);
  },
  upload: async <T = any>(...args: Parameters<ApiClient['upload']>) => {
    if (!apiClientInstance) {
      apiClientInstance = new ApiClient();
    }
    return apiClientInstance.upload<T>(...args);
  },
};

export default apiClient;